import React, { ComponentProps } from 'react';
import { Box, Carousel, Link, Text } from '@stitch-fix/mode-react';
import classNames from 'classnames';
import {
  CloudinaryImageWrapper,
  type CloudinaryImageFields,
} from '../CloudinaryImageWrapper';
import { CloudinaryVideoPlayer } from '../CloudinaryVideoPlayer';
import styles from './styles.module.scss';
import { Header } from '../Header';

interface MerchItem {
  bottomText?: string;
  image?: CloudinaryImageFields;
  href?: string;
  textBgColor?: 'white';
  title?: string;
  topText?: string;
  video?: {
    src: string;
    videoDescription?: string;
  };
}

export interface MerchCarouselProps {
  items: MerchItem[];
  merchHeader?: ComponentProps<typeof Header>;
}

export const MerchCarousel = ({ items, merchHeader }: MerchCarouselProps) => {
  return (
    <Box data-testid="merch-carousel">
      {merchHeader && <Header {...merchHeader} />}
      <Box className={styles.carouselContainer}>
        <Carousel
          ariaLabel="Merch Carousel"
          slideWidth={{ sm: '335px' }}
          gutterOverflow={{ sm: 1.5, md: 3, lg: 0 }}
        >
          {items.map(item => {
            const { bottomText, image, textBgColor, title, topText, video } =
              item;
            const textVariant =
              textBgColor === 'white' ? 'knockout' : undefined;

            let media: React.ReactElement | null = null;

            if (image) {
              media = (
                <CloudinaryImageWrapper
                  alt={image.alt}
                  src={image.src}
                  aspectRatio={image.aspectRatio}
                  lazy
                  sources={{
                    sm: { w: 335 },
                  }}
                  cloudinaryAsset={image.cloudinaryAsset}
                  sizes={[{ minWidth: 0, staticWidth: 335 }]}
                  imageProps={{ loading: 'lazy' }}
                />
              );
            }

            if (video) {
              media = (
                <CloudinaryVideoPlayer
                  videoUrl={video.src}
                  playPauseOnScroll
                  playPauseButtonSize="small"
                  videoDescription={video.videoDescription}
                />
              );
            }

            return (
              <Box
                key={(image && image.src) || (video && video.src)}
                className={styles.itemContainer}
              >
                {item.href ? <Link href={item.href}>{media}</Link> : media}
                {title && (
                  <Box className={styles.titleContainer}>
                    <Text
                      align="center"
                      aria-hidden={video && !video.videoDescription}
                      setting="title-xsmall"
                    >
                      {title}
                    </Text>
                  </Box>
                )}
                {topText && (
                  <Box
                    className={classNames(styles.topTextContainer, {
                      [styles.knockout]: textVariant === 'knockout',
                    })}
                  >
                    <Text
                      align="center"
                      setting="title-xsmall"
                      variant={textVariant}
                    >
                      {topText}
                    </Text>
                  </Box>
                )}
                {bottomText && (
                  <Box
                    className={classNames(styles.bottomTextContainer, {
                      [styles.knockout]: textVariant === 'knockout',
                    })}
                  >
                    <Text
                      align="center"
                      setting="body-small-fixed"
                      variant={textVariant}
                    >
                      {bottomText}
                    </Text>
                  </Box>
                )}
              </Box>
            );
          })}
        </Carousel>
      </Box>
    </Box>
  );
};
